import { useStyletron } from '@thanx/uikit/theme'
import { buildTranslate } from 'locales'
import React, { KeyboardEvent, useEffect, useState } from 'react'
import { merchantTheme } from 'theme'

type PropsT = {
  currentPage: number
  maxPage: number
  onPageChange: (page: number) => void
}

const t = buildTranslate('general')

const PageInputField: React.FC<PropsT> = ({
  currentPage,
  maxPage,
  onPageChange,
}: PropsT) => {
  const [css] = useStyletron()
  const [inputValue, setInputValue] = useState(String(currentPage))

  useEffect(() => {
    setInputValue(String(currentPage))
  }, [currentPage])

  const validateAndNavigate = () => {
    const pageNumber = parseInt(inputValue, 10)

    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > maxPage) {
      setInputValue(String(currentPage))
      return
    }

    if (pageNumber !== currentPage) {
      onPageChange(pageNumber)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateAndNavigate()
      ;(e.target as HTMLInputElement).blur()
    }
  }

  const handleBlur = () => {
    validateAndNavigate()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setInputValue(value)
    }
  }

  const containerStyle = css({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: merchantTheme.colors.grey90,
  })

  const inputStyle = css({
    width: '60px',
    height: '36px',
    padding: '0 8px',
    textAlign: 'center',
    backgroundColor: 'transparent',
    outline: `1px ${merchantTheme.colors.grey30} solid`,
    border: 'none',
    borderRadius: '4px',
    color: merchantTheme.colors.grey90,
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'background-color 0.2s, outline-color 0.2s',
    ':hover': {
      backgroundColor: merchantTheme.colors.grey10,
    },
    ':focus': {
      backgroundColor: merchantTheme.colors.grey10,
      outline: `1px ${merchantTheme.colors.grey90} solid`,
    },
  })

  const labelStyle = css({
    fontSize: '14px',
    color: merchantTheme.colors.grey90,
    whiteSpace: 'nowrap',
  })

  return (
    <div className={containerStyle}>
      <span className={labelStyle}>{t('page')}</span>
      <input
        type="text"
        className={inputStyle}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-label="Page number"
        inputMode="numeric"
      />
      <span className={labelStyle}>{t('page_count', { maxPage })}</span>
    </div>
  )
}

export default PageInputField
